import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { useRef, useEffect } from 'react';
import { cardHover } from '@/animations/variants';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  prefix?: string;
  suffix?: string;
  delay?: number;
}

export default function MetricCard({
  title,
  value,
  subtitle,
  prefix = "",
  suffix = "",
  delay = 0,
}: MetricCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  useEffect(() => {
    if (isInView) {
      const controls = animate(count, typeof value === 'number' ? value : 0, {
        duration: 1.5,
        delay: delay,
        ease: "easeOut",
      });

      return controls.stop;
    }
  }, [isInView, count, value, delay]);


  return (
    <motion.div
      ref={ref}
      className="bg-surface border border-border rounded-xl p-4 sm:p-6 lg:p-8 text-center hover:border-primary/50 hover:shadow-lg transition-all duration-normal"
      variants={cardHover}
      whileHover="whileHover"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay: delay }}
    >
      {/* Value */}
      <div className="mb-2">
        <span className="text-2xl sm:text-3xl md:text-4xl font-accent font-bold text-primary">
          {prefix}
          {typeof value === 'number' ? (
            <motion.span>{rounded}</motion.span>
          ) : (
            value
          )}
          {suffix}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base sm:text-lg font-heading font-semibold text-textPrimary mb-2">
        {title}
      </h3>

      {/* Subtitle */}
      <p className="text-xs sm:text-sm text-textSecondary">
        {subtitle}
      </p>

      {/* Decorative Element */}
      <motion.div
        className="w-8 sm:w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto mt-3 sm:mt-4"
        initial={{ width: 0 }}
        animate={isInView ? { width: 48 } : { width: 0 }}
        transition={{ duration: 0.8, delay: delay + 0.3 }}
      />
    </motion.div>
  );
}